import { createAuthClient } from "better-auth/react";

export const AUTH_TOKEN_KEY = "hey404:auth-token";

export const normalizeApiUrl = (value?: string) => {
  const candidate = value?.trim().replace(/\/+$/, "");
  if (!candidate) return "https://api.hey404.com";
  if (/^https?:\/\//i.test(candidate)) return new URL(candidate).origin;

  const hostname = candidate.split(":")[0]?.toLowerCase();
  const local =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname === "[::1]";
  return new URL(`${local ? "http" : "https"}://${candidate}`).origin;
};

export const apiUrl = normalizeApiUrl(import.meta.env.VITE_API_URL);

export const authClient = createAuthClient({
  baseURL: apiUrl,
  fetchOptions: {
    credentials: "include",
    auth: {
      type: "Bearer",
      token: () => localStorage.getItem(AUTH_TOKEN_KEY) ?? undefined,
    },
    onSuccess(context) {
      const token = context.response.headers.get("set-auth-token");
      if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
    },
  },
});

export const saveAuthToken = (token?: string | null) => {
  if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
};

export const clearAuthToken = () => localStorage.removeItem(AUTH_TOKEN_KEY);

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20_000);
  const abort = () => controller.abort();
  init?.signal?.addEventListener("abort", abort, { once: true });
  let response: Response;
  try {
    response = await fetch(`${apiUrl}${path}`, {
      ...init,
      signal: controller.signal,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
  } finally {
    window.clearTimeout(timeout);
    init?.signal?.removeEventListener("abort", abort);
  }
  if (!response.ok) {
    // Keep the parsed error body on the thrown error so callers can show why a
    // request failed (e.g. a publish blocked by the publication checklist),
    // without changing the message strings existing catches match on.
    const body = (await response.json().catch(() => null)) as unknown;
    const error = new Error(
      response.status === 403
        ? "forbidden"
        : response.status === 429
          ? "rate_limited"
          : "request_failed",
    ) as Error & { status: number; body: unknown };
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return response.status === 204 ? (undefined as T) : response.json();
}

export async function uploadVideo(
  file: File,
  lessonId: string,
): Promise<string> {
  const data = new FormData();
  data.set("video", file);
  data.set("lessonId", lessonId);
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const response = await fetch(`${apiUrl}/api/admin/videos`, {
    method: "POST",
    body: data,
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error("upload_failed");
  const result = (await response.json()) as { url: string };
  return result.url;
}

// Uploads a lesson-resource file (kind file/template) and returns its stored
// URL, to drop straight into the resource's `url`. Mirrors uploadVideo.
export async function uploadResource(
  file: File,
  lessonId: string,
): Promise<string> {
  const data = new FormData();
  data.set("file", file);
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const response = await fetch(
    `${apiUrl}/api/admin/learning-tools/lessons/${lessonId}/resources/upload`,
    {
      method: "POST",
      body: data,
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new Error(body?.error ?? "upload_failed");
  }
  const result = (await response.json()) as { url: string };
  return result.url;
}

// Uploads a public image (instructor/school photo) and returns its /api/avatars/
// URL, to save into the entity's avatarUrl/imageUrl. Mirrors uploadResource.
export async function uploadImage(file: File): Promise<string> {
  const data = new FormData();
  data.set("file", file);
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const response = await fetch(`${apiUrl}/api/admin/learning-tools/images`, {
    method: "POST",
    body: data,
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new Error(body?.error ?? "upload_failed");
  }
  const result = (await response.json()) as { url: string };
  return result.url;
}

export async function downloadAuthenticatedFile(
  path: string,
  filename: string,
) {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const response = await fetch(`${apiUrl}${path}`, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error("download_failed");
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
